/**
 * product-hover-zoom.js
 *
 * Hover zoom with right-side preview panel + click/keyboard fullscreen lightbox.
 *
 * This is the single zoom experience for the product gallery. The theme's
 * native zoom-dialog is disabled (block_settings.zoom = false), so this file
 * is solely responsible for zooming product images.
 *
 * Hover behavior (desktop / fine pointer only):
 *   - A selection rectangle appears on the main image where the cursor is
 *   - A separate preview panel to the right shows the magnified area
 *
 * Open behavior (all devices):
 *   - Click OR Enter/Space on a focused image opens a fullscreen lightbox
 *   - Built on a native <dialog> (showModal): browser-managed focus trap,
 *     inert background, return-focus on close, and Escape-to-close come free
 *   - Prev/next navigation via buttons, arrow keys, and swipe
 *
 * Accessibility (WCAG 2.1 AA):
 *   - Each zoomable image is an operable, labelled control (role/tabindex)
 *   - Decorative hover overlays are aria-hidden
 *   - A polite live region announces the current image
 *   - Focus is trapped while open and returned to the trigger on close
 */

// ── Configuration ─────────────────────────────────────────────────────────

const SWIPE_THRESHOLD = 50; // Min px drag to count as a swipe

// Localised labels (read from <media-gallery data-*> with English fallbacks)
let ZOOM_LABEL = 'Open image in full screen';
let CLOSE_LABEL = 'Close';

// ── Hover Zoom: Selector + Preview Panel ──────────────────────────────────

/**
 * Attach hover-zoom behavior to a single <media-gallery>.
 * Creates one shared preview panel per gallery and selection rectangles
 * per image container.
 *
 * @param {HTMLElement} gallery - A <media-gallery> element
 */
function initHoverZoom(gallery) {
  if (gallery._hzHoverInit) return;
  gallery._hzHoverInit = true;

  // Create the preview panel (one per gallery, appended to body).
  // Purely decorative — hidden from assistive technology.
  const preview = document.createElement('div');
  preview.className = 'hz-zoom-preview';
  preview.setAttribute('aria-hidden', 'true');
  document.body.appendChild(preview);

  // Track which container is currently active
  let activeContainer = null;

  const imageContainers = gallery.querySelectorAll(
    '.product-media-container--image .product-media'
  );

  imageContainers.forEach((mediaEl) => {
    const img = mediaEl.querySelector('img.product-media__image');
    if (!img) return;

    mediaEl.classList.add('hz-zoom-container');

    // Create the selection rectangle for this image (decorative)
    const selector = document.createElement('div');
    selector.className = 'hz-zoom-selector';
    selector.setAttribute('aria-hidden', 'true');
    mediaEl.appendChild(selector);

    mediaEl.addEventListener('mouseenter', () => {
      activeContainer = mediaEl;

      const hiRes = img.getAttribute('data_max_resolution') || img.currentSrc || img.src;
      preview.style.backgroundImage = `url(${hiRes})`;

      positionPreviewPanel(gallery, preview);
      mediaEl.classList.add('hz-zoom-active');
      // Preview is on document.body — toggle visibility directly
      preview.classList.add('hz-zoom-preview--visible');
    });

    mediaEl.addEventListener('mouseleave', () => {
      mediaEl.classList.remove('hz-zoom-active');
      preview.classList.remove('hz-zoom-preview--visible');
      activeContainer = null;
    });

    mediaEl.addEventListener('mousemove', (e) => {
      if (activeContainer !== mediaEl) return;
      updateZoom(e, mediaEl, selector, preview);
    });
  });
}

/**
 * Position the preview panel over the product details column (right side).
 * Falls back to placing it to the right of the gallery if no details
 * column is found.  Uses fixed positioning to escape overflow:hidden.
 */
function positionPreviewPanel(gallery, preview) {
  const galleryRect = gallery.getBoundingClientRect();

  // Find the product details column — the sibling grid cell
  const gridContainer = gallery.closest('.product-information__grid');
  const detailsCol = gridContainer?.querySelector('.product-details');

  // Preview dimensions come from CSS (460x460 default, responsive breakpoints)
  preview.style.position = 'fixed';

  if (detailsCol) {
    const detailsRect = detailsCol.getBoundingClientRect();
    preview.style.left = `${detailsRect.left}px`;
    preview.style.top = `${galleryRect.top}px`;
  } else {
    preview.style.left = `${galleryRect.right + 8}px`;
    preview.style.top = `${galleryRect.top}px`;
  }
  // Width and height are set by CSS classes, not inline styles
}

/**
 * Update the selection rectangle position and preview panel background
 * based on cursor position.
 */
function updateZoom(e, container, selector, preview) {
  const rect = container.getBoundingClientRect();

  // Read actual rendered sizes from the DOM (set by CSS, not inline)
  const previewW = preview.offsetWidth;
  const previewH = preview.offsetHeight;
  const selW = selector.offsetWidth;   // 130px from CSS
  const selH = selector.offsetHeight;  // 130px from CSS

  // Cursor position relative to the container (0–1)
  const xRatio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  const yRatio = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

  // Center the selector on the cursor, clamp to container bounds
  let selX = xRatio * rect.width - selW / 2;
  let selY = yRatio * rect.height - selH / 2;
  selX = Math.max(0, Math.min(rect.width - selW, selX));
  selY = Math.max(0, Math.min(rect.height - selH, selY));

  // Position the selection rectangle
  selector.style.left = `${selX}px`;
  selector.style.top = `${selY}px`;

  // Zoom ratio: how much of the source image the selector covers
  const zoomX = previewW / selW;
  const zoomY = previewH / selH;

  // Background size = source image scaled up by the zoom ratio
  const bgW = rect.width * zoomX;
  const bgH = rect.height * zoomY;

  // Background offset: map selector position to background position
  const bgXRatio = selX / (rect.width - selW || 1);
  const bgYRatio = selY / (rect.height - selH || 1);
  const bgX = -(bgXRatio * (bgW - previewW));
  const bgY = -(bgYRatio * (bgH - previewH));

  preview.style.backgroundSize = `${bgW}px ${bgH}px`;
  preview.style.backgroundPosition = `${bgX}px ${bgY}px`;
}

// ── Fullscreen Lightbox (native <dialog>) ─────────────────────────────────

/** @type {HTMLDialogElement | null} */
let lightbox = null;
let lbImages = [];
let lbIndex = 0;
/** @type {HTMLElement | null} */
let lbTrigger = null; // element to return focus to on close
/** @type {HTMLElement | null} */
let lbStatus = null;  // polite live region

function ensureLightbox() {
  if (lightbox) return;

  lightbox = /** @type {HTMLDialogElement} */ (document.createElement('dialog'));
  lightbox.className = 'hz-lightbox';
  // A modal dialog needs an accessible name; refined per-image in showLightboxImage().
  lightbox.setAttribute('aria-label', ZOOM_LABEL);
  lightbox.innerHTML = `
    <button type="button" class="hz-lightbox__close" aria-label="${CLOSE_LABEL}">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
    <button type="button" class="hz-lightbox__nav hz-lightbox__prev" aria-label="Previous image">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    </button>
    <div class="hz-lightbox__image-wrap">
      <img class="hz-lightbox__image" src="" alt="" />
    </div>
    <button type="button" class="hz-lightbox__nav hz-lightbox__next" aria-label="Next image">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    </button>
    <div class="hz-lightbox__counter" aria-hidden="true"></div>
    <p class="hz-lightbox__status visually-hidden" aria-live="polite"></p>
  `;

  document.body.appendChild(lightbox);

  const closeBtn = lightbox.querySelector('.hz-lightbox__close');
  const prevBtn = lightbox.querySelector('.hz-lightbox__prev');
  const nextBtn = lightbox.querySelector('.hz-lightbox__next');
  const imgEl = lightbox.querySelector('.hz-lightbox__image');
  lbStatus = lightbox.querySelector('.hz-lightbox__status');

  closeBtn.addEventListener('click', closeLightbox);

  // Click on the backdrop / outside the image closes the lightbox
  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox || e.target.classList.contains('hz-lightbox__image-wrap')) {
      closeLightbox();
    }
  });

  prevBtn.addEventListener('click', (e) => { e.stopPropagation(); navigateLightbox(-1); });
  nextBtn.addEventListener('click', (e) => { e.stopPropagation(); navigateLightbox(1); });

  // Arrow-key navigation. Escape is handled natively by <dialog> (cancel/close).
  // Focus is trapped inside the dialog by showModal(), so listening here suffices.
  lightbox.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); navigateLightbox(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); navigateLightbox(1); }
  });

  // Native close (Escape, backdrop, or .close()) — restore page state + focus.
  lightbox.addEventListener('close', () => {
    document.body.style.overflow = '';
    if (lbTrigger && typeof lbTrigger.focus === 'function') lbTrigger.focus();
    lbTrigger = null;
  });

  // Swipe navigation
  let touchStartX = 0;
  imgEl.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].clientX;
  }, { passive: true });
  imgEl.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > SWIPE_THRESHOLD) navigateLightbox(dx < 0 ? 1 : -1);
  }, { passive: true });
}

/**
 * @param {{src: string, alt: string}[]} images
 * @param {number} startIndex
 * @param {HTMLElement} [trigger] - element to return focus to on close
 */
function openLightbox(images, startIndex, trigger) {
  ensureLightbox();
  if (lightbox.open) return;

  lbImages = images;
  lbIndex = startIndex;
  lbTrigger = trigger || null;

  showLightboxImage();
  document.body.style.overflow = 'hidden';
  lightbox.showModal(); // browser traps focus + makes the rest of the page inert
  lightbox.querySelector('.hz-lightbox__close').focus();
}

function closeLightbox() {
  // .close() fires the native 'close' event → cleanup runs there
  if (lightbox && lightbox.open) lightbox.close();
}

function navigateLightbox(dir) {
  const next = lbIndex + dir;
  if (next < 0 || next >= lbImages.length) return;
  lbIndex = next;
  showLightboxImage();
}

function showLightboxImage() {
  const imgEl = lightbox.querySelector('.hz-lightbox__image');
  const counter = lightbox.querySelector('.hz-lightbox__counter');
  const prevBtn = lightbox.querySelector('.hz-lightbox__prev');
  const nextBtn = lightbox.querySelector('.hz-lightbox__next');
  const item = lbImages[lbIndex];
  const position = `${lbIndex + 1} / ${lbImages.length}`;

  imgEl.classList.add('hz-lightbox__image--loading');
  imgEl.src = item.src;
  imgEl.alt = item.alt || '';
  imgEl.onload = () => imgEl.classList.remove('hz-lightbox__image--loading');

  counter.textContent = position;

  // Keep the dialog's accessible name meaningful and announce changes politely
  lightbox.setAttribute('aria-label', item.alt || ZOOM_LABEL);
  if (lbStatus) lbStatus.textContent = item.alt ? `${item.alt} (${position})` : position;

  prevBtn.disabled = lbIndex === 0;
  nextBtn.disabled = lbIndex === lbImages.length - 1;
}

// ── Gallery Initialisation ──────────────────────────────────────────────

function initGallery(gallery) {
  if (gallery._hzGalleryInit) return;
  gallery._hzGalleryInit = true;

  // Read localised labels once (same locale across the page)
  if (gallery.dataset.hzZoomLabel) ZOOM_LABEL = gallery.dataset.hzZoomLabel;
  if (gallery.dataset.hzCloseLabel) CLOSE_LABEL = gallery.dataset.hzCloseLabel;

  const imageContainers = gallery.querySelectorAll(
    '.product-media-container--image .product-media'
  );

  // Build images list for the lightbox
  const images = [];
  imageContainers.forEach((mediaEl) => {
    const img = mediaEl.querySelector('img.product-media__image');
    if (!img) return;
    images.push({
      src: img.getAttribute('data_max_resolution') || img.currentSrc || img.src,
      alt: img.alt || '',
    });
  });

  // Hover zoom (desktop — hidden on touch via CSS)
  initHoverZoom(gallery);

  // Open the lightbox via pointer OR keyboard. Each image is exposed as an
  // operable, labelled control (the theme's native zoom button is disabled).
  imageContainers.forEach((mediaEl, idx) => {
    mediaEl.setAttribute('role', 'button');
    mediaEl.setAttribute('tabindex', '0');
    mediaEl.setAttribute('aria-label', ZOOM_LABEL);

    const open = (e) => {
      // Let nested interactive media (video, 3D, links) handle their own clicks
      if (e.target.closest('button:not([role]), a, video, model-viewer, deferred-media')) return;
      e.preventDefault();
      e.stopPropagation();
      openLightbox(images, idx, mediaEl);
    };

    mediaEl.addEventListener('click', open);
    mediaEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') open(e);
    });
  });
}

// ── Bootstrap ───────────────────────────────────────────────────────────

function initAll() {
  document.querySelectorAll('media-gallery').forEach(initGallery);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', initAll);
document.addEventListener('shopify:block:load', initAll);
